import { useCallback, useEffect, useState } from "react";
import { api, errDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Factory, Plus, Link2, X, ExternalLink } from "lucide-react";

const SC_STATUS = {
  open: ["bg-gray-100 text-gray-700", "Offen"], in_8d: ["bg-amber-100 text-amber-800", "8D läuft"],
  effectiveness_pending: ["bg-blue-100 text-blue-700", "Wirksamkeit"], closed: ["bg-green-100 text-green-700", "Geschlossen"],
};

export function LinkedSupplierComplaints({ complaintId, defectCategory }) {
  const [rows, setRows] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/complaints/${complaintId}/linked-supplier-complaints`).then((r) => setRows(r.data || [])).catch(() => {});
  }, [complaintId]);
  useEffect(() => {
    load();
    api.get("/srm/my-caps").then((r) => {
      const caps = r.data.caps || [];
      setCanManage(caps.includes("complaint_manage") || caps.includes("srm_admin"));
    }).catch(() => {});
  }, [load]);

  const unlink = async (scid) => {
    try { await api.delete(`/complaints/${complaintId}/link-supplier-complaint/${scid}`); load(); }
    catch (e) { toast.error(errDetail(e)); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="linked-supplier-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-1.5"><Factory className="h-3.5 w-3.5" />Lieferantenreklamationen</h3>
        {canManage && (
          <div className="flex gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setLinkOpen(true)} data-testid="link-sc-btn">
              <Link2 className="h-3 w-3 mr-1" />Verknüpfen
            </Button>
            <Button size="sm" className="h-7 text-xs bg-[#1B2A4A] hover:bg-[#24365d]" onClick={() => setCreateOpen(true)} data-testid="create-sc-btn">
              <Plus className="h-3 w-3 mr-1" />Erstellen
            </Button>
          </div>
        )}
      </div>
      {rows.length === 0 && <p className="text-xs text-gray-400">Keine verknüpfte Lieferantenreklamation.</p>}
      <div className="space-y-2">
        {rows.map((r) => {
          const [cls, lbl] = SC_STATUS[r.status] || ["bg-gray-100 text-gray-600", r.status];
          return (
            <div key={r.id} className="flex items-center gap-2 border border-gray-100 rounded-lg p-2 text-sm" data-testid="linked-sc-item">
              <a href={`/supplier-complaints?id=${r.id}`} className="font-mono font-medium text-[#1B2A4A] hover:underline flex items-center gap-1">
                {r.complaint_number}<ExternalLink className="h-3 w-3" />
              </a>
              <span className="text-gray-600 truncate flex-1">{r.supplier_name} · {r.defect_category || "–"}</span>
              <Badge className={cls}>{lbl}</Badge>
              {canManage && <button onClick={() => unlink(r.id)} data-testid="unlink-sc-btn" className="text-gray-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>}
            </div>
          );
        })}
      </div>
      {createOpen && <CreateDialog complaintId={complaintId} defectCategory={defectCategory} onClose={(ch) => { setCreateOpen(false); if (ch) load(); }} />}
      {linkOpen && <LinkDialog complaintId={complaintId} linkedIds={rows.map((r) => r.id)} onClose={(ch) => { setLinkOpen(false); if (ch) load(); }} />}
    </div>
  );
}

function CreateDialog({ complaintId, defectCategory, onClose }) {
  const [suppliers, setSuppliers] = useState([]);
  const [form, setForm] = useState({ supplier_id: "", defect_category: defectCategory || "", severity: "Major", description: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/suppliers?status=approved").then((r) => setSuppliers(r.data || [])).catch(() => {}); }, []);
  const save = async () => {
    if (!form.supplier_id) { toast.error("Lieferant erforderlich"); return; }
    setBusy(true);
    try { await api.post(`/complaints/${complaintId}/create-supplier-complaint`, form); toast.success("Lieferantenreklamation erstellt"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="create-sc-dialog">
        <DialogHeader><DialogTitle>Lieferantenreklamation erstellen</DialogTitle>
          <DialogDescription>Vorausgefüllt aus dieser Kundenreklamation, automatisch verknüpft.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Lieferant *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => setForm((f) => ({ ...f, supplier_id: v }))}>
              <SelectTrigger data-testid="create-sc-supplier"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Defektkategorie</Label><Input value={form.defect_category} onChange={(e) => setForm((f) => ({ ...f, defect_category: e.target.value }))} /></div>
            <div><Label>Schwere</Label>
              <Select value={form.severity} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["Minor", "Major", "Critical"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div><Label>Beschreibung</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Leer = automatisch aus Kundenreklamation" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
          <Button data-testid="create-sc-save" disabled={busy} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Erstellen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkDialog({ complaintId, linkedIds, onClose }) {
  const [all, setAll] = useState([]);
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/srm/complaints").then((r) => setAll((r.data || []).filter((x) => !linkedIds.includes(x.id)))).catch(() => {}); }, []);
  const save = async () => {
    if (!sel) return;
    setBusy(true);
    try { await api.post(`/complaints/${complaintId}/link-supplier-complaint`, { supplier_complaint_id: sel }); toast.success("Verknüpft"); onClose(true); }
    catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md" data-testid="link-sc-dialog">
        <DialogHeader><DialogTitle>Bestehende Lieferantenreklamation verknüpfen</DialogTitle>
          <DialogDescription>Wählen Sie eine vorhandene SREK zur Verknüpfung.</DialogDescription></DialogHeader>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger data-testid="link-sc-select"><SelectValue placeholder="Reklamation wählen…" /></SelectTrigger>
          <SelectContent>{all.map((x) => <SelectItem key={x.id} value={x.id}>{x.complaint_number} · {x.supplier_name}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter><Button variant="outline" onClick={() => onClose(false)}>Abbrechen</Button>
          <Button data-testid="link-sc-save" disabled={busy || !sel} className="bg-[#1B2A4A] hover:bg-[#24365d]" onClick={save}>Verknüpfen</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
